import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  X, 
  Send, 
  Minimize2, 
  Maximize2, 
  Bot, 
  User,
  Loader2,
  HelpCircle
} from 'lucide-react';

const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const SupportChatbot = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Carregar conversa existente ou iniciar nova
  useEffect(() => {
    const savedConversationId = localStorage.getItem('support_conversation_id');
    if (savedConversationId) {
      setConversationId(savedConversationId);
      loadConversation(savedConversationId);
    }
  }, []);

  // Scroll para última mensagem
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus no input quando abrir
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const loadConversation = async (convId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/support/chat/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.messages?.length > 0) {
        setMessages(response.data.messages.map(m => ({
          id: m.id,
          type: m.role === 'user' ? 'user' : 'bot',
          text: m.message,
          timestamp: new Date(m.timestamp)
        })));
      }
    } catch (error) {
      // Conversa não encontrada, iniciar nova
      localStorage.removeItem('support_conversation_id');
      setConversationId(null);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;
    
    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/support/chat`,
        {
          message: userMessage.text,
          conversation_id: conversationId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Salvar conversation_id
      if (response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
        localStorage.setItem('support_conversation_id', response.data.conversation_id);
      }
      
      // Adicionar resposta do bot se houver
      if (response.data.bot_response) {
        const botMessage = {
          id: response.data.bot_response.id,
          type: 'bot',
          text: response.data.bot_response.message,
          timestamp: new Date(response.data.bot_response.timestamp)
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const quickReplies = [
    '💳 Pagamento',
    '🔐 Login',
    '📋 Verificação',
    '💰 Saque'
  ];

  // Não mostrar para admins (eles têm painel próprio)
  if (user?.role === 'admin') return null;

  return (
    <>
      {/* Floating Button - Posicionado no canto ESQUERDO para não conflitar com o FAB de criar post */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-20 left-4 z-40 w-12 h-12 rounded-full bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] shadow-lg flex items-center justify-center text-white hover:shadow-[0_0_20px_rgba(107,45,92,0.5)] transition-shadow border border-[#C79F5E]/30"
            data-testid="support-chat-button"
          >
            <MessageCircle className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window - Também no lado esquerdo */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ 
              opacity: 1, 
              y: 0, 
              scale: 1,
              height: isMinimized ? 'auto' : 'auto'
            }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className={`fixed bottom-20 left-4 z-40 w-[350px] max-w-[calc(100vw-2rem)] bg-[#1A1A1A] rounded-2xl shadow-2xl border border-[#C79F5E]/30 overflow-hidden ${
              isMinimized ? 'h-auto' : 'h-[500px] max-h-[70vh]'
            }`}
            data-testid="support-chat-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#4D1A4A] to-[#6B2D5C] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C79F5E] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-black" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Suporte LYBERHOT</h3>
                  <p className="text-xs text-white/60">Online • Resposta rápida</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  {isMinimized ? (
                    <Maximize2 className="w-4 h-4 text-white" />
                  ) : (
                    <Minimize2 className="w-4 h-4 text-white" />
                  )}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            {!isMinimized && (
              <>
                <div className="flex-1 h-[320px] overflow-y-auto p-4 space-y-4">
                  {/* Welcome Message */}
                  {messages.length === 0 && (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] flex items-center justify-center">
                        <HelpCircle className="w-8 h-8 text-black" />
                      </div>
                      <h4 className="font-semibold text-white mb-2">Olá, {user?.name?.split(' ')[0] || 'visitante'}!</h4>
                      <p className="text-gray-400 text-sm mb-4">
                        Como posso ajudar você hoje?
                      </p>
                      {/* Quick Replies */}
                      <div className="flex flex-wrap gap-2 justify-center">
                        {quickReplies.map((reply, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setInputValue(reply.split(' ')[1]);
                              setTimeout(() => inputRef.current?.focus(), 100);
                            }}
                            className="px-3 py-1.5 bg-[#0D0D0D] border border-[#C79F5E]/30 rounded-full text-xs text-gray-300 hover:border-[#C79F5E] hover:text-[#C79F5E] transition-all"
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${
                        message.type === 'user' 
                          ? 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black' 
                          : 'bg-[#2A2A2A] text-white'
                      } rounded-2xl px-4 py-2.5`}>
                        {message.type === 'bot' && (
                          <div className="flex items-center gap-2 mb-1">
                            <Bot className="w-3 h-3 text-[#C79F5E]" />
                            <span className="text-xs text-[#C79F5E]">Suporte</span>
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                        <p className={`text-[10px] mt-1 ${
                          message.type === 'user' ? 'text-black/60' : 'text-gray-500'
                        }`}>
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    </motion.div>
                  ))}

                  {/* Loading */}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-[#2A2A2A] rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 text-[#C79F5E] animate-spin" />
                          <span className="text-sm text-gray-400">Digitando...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="p-4 border-t border-[#2A2A2A]">
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Digite sua mensagem..."
                      className="flex-1 bg-[#0D0D0D] border border-[#2A2A2A] rounded-full px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#C79F5E] transition-colors"
                      data-testid="support-chat-input"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!inputValue.trim() || loading}
                      className="w-10 h-10 rounded-full bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] flex items-center justify-center text-black disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                      data-testid="support-chat-send"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportChatbot;
